import {
  AutomationConfig_UserStatus,
  Job_RetryStatus_CanBeRetried,
  JobStatus_Stage,
} from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import type { LimitedJob } from "../../services/engineClient";
import { useEngineClient } from "../../services/engineClient";
import { useRootStore } from "../../stores/hooks";
import { jobsToRecords } from "./ActivityGrid/ActivityGrid.helpers";
import { translateIfPresent } from "../../locales/i18n";

interface RetryFunctions {
  retry: (
    jobId: string,
    onRetry?: () => void,
    onClose?: () => void,
  ) => Promise<void>;
  isJobCanBeRetried: (jobState: LimitedJob) => boolean;
}

export const useJobRetry = (): RetryFunctions => {
  const { automations, jobsStore, modal, errorModal, integrations, limits } =
    useRootStore();
  const engineClient = useEngineClient();

  const isJobCanBeRetried = (jobState: LimitedJob): boolean => {
    if (jobState.retryStatus?.canBeRetried !== undefined) {
      // if Yes and if its not set
      return (
        jobState.retryStatus.canBeRetried !== Job_RetryStatus_CanBeRetried.NO
      );
    } else if (
      jobState.status?.stage === JobStatus_Stage.ACTION_FAIL &&
      !limits.isOverLimit()
    ) {
      // backward compatibility
      return true;
    }
    return false;
  };

  const retry = async (
    jobId: string,
    onRetry?: () => void,
    onClose?: () => void,
  ): Promise<void> => {
    if (
      automations.candidate.self.userStatus ===
      AutomationConfig_UserStatus.USER_DISABLED
    ) {
      modal.openConfirmModal({
        type: "confirm",
        title: t("confirm-modal.automation-is-off"),
        description: t("retry.would-you-like-to-retry"),
        positiveButtonText: t("caption.Yes"),
        negativeButtonText: t("caption.No"),
        showCheckbox: false,
        onClose: () => {
          modal.closeConfirmModal();
          onClose?.();
        },
        onConfirm: () => {
          void innerRetry(jobId);
          modal.closeConfirmModal();
          onRetry?.();
        },
      });
      return;
    }
    await innerRetry(jobId);
    onRetry?.();
  };

  const innerRetry = async (jobId: string): Promise<void> => {
    jobsStore.setLoading(true);
    try {
      const retriedJob = await engineClient.retryJob({ jobId });
      if (retriedJob.job !== undefined) {
        jobsStore.setJobs([
          ...jobsToRecords([retriedJob.job], isJobCanBeRetried),
          ...jobsStore.jobs,
        ]);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      errorModal.openModal({
        title: t("Error happened on retry"),
        description: translateIfPresent(
          `caption.${errorMessage}`,
          errorMessage,
        ),
        onClose: () => {
          errorModal.closeModal();
        },
      });
    } finally {
      jobsStore.setLoading(false);
    }
  };

  return {
    retry,
    isJobCanBeRetried,
  };
};
